"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageFrame = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const clsx_1 = __importDefault(require("clsx"));
const react_1 = require("react");
const web_blizzard_reporting_1 = require("@snapchat/web-blizzard-reporting");
const web_extraction_1 = require("@snapchat/web-extraction");
const web_blizzard_reporting_hooks_1 = require("@snapchat/web-blizzard-reporting-hooks");
const hooks_1 = require("../../utils/hooks");
const Navigation_1 = require("../Navigation");
const WebChatUpsellToast_1 = require("../WebChatUpsellToast");
const CTAFooter_1 = require("../CTAFooter");
const PageFrame_module_css_1 = __importDefault(require("./PageFrame.module.css"));
const CommonPageFrame_module_css_1 = __importDefault(require("./CommonPageFrame.module.css"));
const MobilePageFrameFooter_1 = require("./MobilePageFrameFooter");
const MobilePageFrameNav_1 = require("./MobilePageFrameNav");
const Shell_1 = require("../Shell");
const web_consumer_nav_1 = require("@snapchat/web-consumer-nav");
const web_sc_cookies_1 = require("@snapchat/web-sc-cookies");
const constants_1 = require("./constants");
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const web_env_1 = require("@snapchat/web-env");
const grafana_1 = require("./grafana");
const LoadingPage_1 = require("../LoadingPage");
/**
 * Provides a common navigation and footer for all apps.
 * Handles responsiveness via CSS.
 */
function PageFrame({ closeByDefault, disableUpsellQRCode, locale, messages, appType, pageType, downloadUrl, webClientId, webPageViewTraceId, children, localeChange, showSnapchatAdsUrl, showCreateStoryLink, disableMobileBottomNav, showWhiteBgOnBottomNav, hasLegacyOISCTA, mobilePageContainerRef, hideMobileNavigation, hideDesktopNavigation, hideDefaultFooter, footerProps, footerExperimentProps, newOISExperimentProps, showParentMicrositeBanner = false, useMinifiedLayout = false, sidebarConfig, }) {
    const mobileNavigationProps = {
        appType,
        disableMobileBottomNav: useMinifiedLayout || disableMobileBottomNav,
        showWhiteBgOnBottomNav,
        hasLegacyOISCTA,
    };
    const desktopNavigationProps = {
        appType,
        showSnapchatAdsUrl,
        showCreateStoryLink,
    };
    const scrollDirection = (0, hooks_1.useScrollDirection)(mobilePageContainerRef);
    // Remove Navigation and Footer from Snapchat In App Browser regardless of the page type
    const showNavOnMobile = !hideMobileNavigation && !(0, web_extraction_1.isSnapchat)();
    const showFooterOnMobile = !hideDefaultFooter && !(0, web_extraction_1.isSnapchat)();
    const showDesktopNavigation = !hideDesktopNavigation;
    return ((0, jsx_runtime_1.jsxs)("main", { className: PageFrame_module_css_1.default.pageFrame, "data-testid": "page-frame", children: [showNavOnMobile && ((0, jsx_runtime_1.jsx)("div", { className: "mobileOnly", children: (0, jsx_runtime_1.jsx)(MobilePageFrameNav_1.MobilePageFrameNav, { navigationProps: mobileNavigationProps, scrollDirection: scrollDirection, locale: locale, messages: messages, pageType: pageType, downloadUrl: downloadUrl, localeChange: localeChange, webClientId: webClientId, webPageViewTraceId: webPageViewTraceId, newOISExperimentProps: newOISExperimentProps, useMinifiedLayout: useMinifiedLayout }) })), (0, jsx_runtime_1.jsxs)(Content, { pageType: pageType, closeUpsellByDefault: closeByDefault, locale: locale, webPageViewTraceId: webPageViewTraceId, disableUpsellQRCode: disableUpsellQRCode, sidebarConfig: sidebarConfig, children: [showDesktopNavigation && ((0, jsx_runtime_1.jsx)("div", { className: "desktopOnly", children: (0, jsx_runtime_1.jsx)(Navigation_1.DesktopNavigation, { ...desktopNavigationProps, locale: locale, messages: messages, localeChange: localeChange, webClientId: webClientId, pageType: pageType, downloadUrl: downloadUrl }) })), children, (0, jsx_runtime_1.jsx)("div", { className: (0, clsx_1.default)(CommonPageFrame_module_css_1.default.footerWrapper, "desktopOnly"), children: !hideDefaultFooter && ((0, jsx_runtime_1.jsx)(CTAFooter_1.CTAFooter, { ...(footerProps ?? {}), locale: locale, downloadUrl: downloadUrl, localeChange: localeChange, webClientId: webClientId, pageType: pageType, showParentMicrositeBanner: showParentMicrositeBanner, isMobile: false })) })] }), showFooterOnMobile && ((0, jsx_runtime_1.jsx)(MobilePageFrameFooter_1.MobilePageFrameFooter, { webClientId: webClientId, locale: locale, localeChange: localeChange, footerProps: footerProps, footerExperimentProps: footerExperimentProps, hideDefaultFooter: hideDefaultFooter, pageType: pageType, downloadUrl: downloadUrl, newOISExperimentProps: newOISExperimentProps, webPageViewTraceId: webPageViewTraceId }))] }));
}
exports.PageFrame = PageFrame;
/**
 * Renders all the page content.
 * For mobile, return its children
 * For desktop, render the sidebar for signed-out users.
 */
function Content({ children, pageType, webPageViewTraceId, locale, disableUpsellQRCode, closeUpsellByDefault, sidebarConfig, }) {
    const webClientId = (0, web_sc_cookies_1.useGetWebClientId)();
    const { authStateObservable } = (0, react_1.useContext)(Shell_1.PageContext);
    const { isMobile } = (0, react_1.useContext)(Shell_1.PageContext);
    // See if we can determine from the Observable's current value first.
    // If we can't, we subscribe to the next value.
    const determineInitialAuthState = (0, react_1.useCallback)(() => {
        if (!authStateObservable)
            return undefined;
        const authStatus = authStateObservable.getCurrentValue().authStatus;
        if (authStatus === web_blizzard_reporting_hooks_1.AuthStatus.UNKNOWN) {
            return undefined;
        }
        return authStatus === web_blizzard_reporting_hooks_1.AuthStatus.AUTHENTICATED;
    }, [authStateObservable]);
    const [isSignedIn, setIsSignedIn] = (0, react_1.useState)(determineInitialAuthState());
    (0, react_1.useEffect)(() => {
        // Update `isSignedin` once - for now we don't want to show / hide the bar as auth status changes.
        if (!authStateObservable || isSignedIn !== undefined)
            return;
        const unsubscribe = authStateObservable.subscribe((state) => {
            if (state.authStatus !== web_blizzard_reporting_hooks_1.AuthStatus.UNKNOWN) {
                setIsSignedIn(state.authStatus === web_blizzard_reporting_hooks_1.AuthStatus.AUTHENTICATED);
            }
        });
        return () => {
            unsubscribe();
        };
    }, [authStateObservable, isSignedIn]);
    (0, react_1.useEffect)(() => {
        if (isSignedIn === true && sidebarConfig?.redirectIfSignedIn && !isMobile) {
            (0, web_blizzard_reporting_1.reportWebAction)({
                actionName: web_blizzard_reporting_1.WebActionName.OpenWebChatCta,
                pageType,
                webClientId: webClientId ?? "",
                actionSource: web_blizzard_reporting_1.WebActionSource.LoggedInRedirect,
            });
            const pageName = web_blizzard_reporting_1.PageType[pageType].toString();
            void (0, web_graphene_reporting_1.incrementCounter)(`${grafana_1.metricsPrefix}.signed_in_redirect`, 1, {
                pageName,
            });
            void (0, web_graphene_reporting_1.flushMetrics)().then((_r) => {
                const w = (0, web_env_1.getWindow)();
                if (w) {
                    w.location.href = "https://web.snapchat.com?ref=homepage_auto_redirect";
                }
            });
        }
    }, [isSignedIn, pageType, webClientId, sidebarConfig, isMobile]);
    // For mobile, we can skip everything below and return the children.
    // TODO: skip above logic as well.
    if (isMobile) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
    }
    const renderSidebar = (() => {
        // override from parent to enable AB testing select pages.
        if (sidebarConfig?.forceShowSidebar) {
            // if we redirect for signed in traffic, we can always show the sidebar
            // we don't need to wait to determine.
            if (sidebarConfig.redirectIfSignedIn) {
                return true;
            }
            return isSignedIn === false;
        }
        if (!constants_1.PAGETYPES_WITH_SIDEBAR.includes(pageType)) {
            return false;
        }
        return isSignedIn === false;
    })();
    const renderUpsell = (() => {
        // if pagetype is not allowed - don't render.
        if (constants_1.PAGE_TYPES_WITHOUT_WEBCHAT_UPSELL.includes(pageType)) {
            return false;
        }
        return isSignedIn;
    })();
    const useLargeVariant = pageType === web_blizzard_reporting_1.PageType.SNAPCHAT_HOME_PAGE;
    // If we're redirecting signed in traffic, render the loading page until we know we are logged out.
    // Prevents a flash when we do homepage > logged in > snapchat web
    if (sidebarConfig?.redirectIfSignedIn && isSignedIn !== false) {
        return (0, jsx_runtime_1.jsx)(LoadingPage_1.LoadingPage, {});
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [renderUpsell && ((0, jsx_runtime_1.jsx)(WebChatUpsellToast_1.WebChatUpsellToast, { webPageViewTraceId: webPageViewTraceId, webClientId: webClientId, pageType: pageType, locale: locale, disableQRCode: disableUpsellQRCode, closeByDefault: closeUpsellByDefault })), (0, jsx_runtime_1.jsxs)("div", { className: (0, clsx_1.default)(PageFrame_module_css_1.default.desktopContentWrapper, {
                    [PageFrame_module_css_1.default.withSidebar]: renderSidebar,
                    [PageFrame_module_css_1.default.largeSidebar]: useLargeVariant,
                }), "data-testid": "desktop-content-wrapper", children: [renderSidebar && ((0, jsx_runtime_1.jsx)("div", { className: PageFrame_module_css_1.default.sidebar, "data-testid": "sign-in-sidebar", children: (0, jsx_runtime_1.jsx)(web_consumer_nav_1.Sidebar, { pageType: pageType, animate: !sidebarConfig?.redirectIfSignedIn }) })), (0, jsx_runtime_1.jsx)("div", { className: PageFrame_module_css_1.default.desktopPageContent, children: children })] })] }));
}
//# sourceMappingURL=PageFrame.js.map